import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import API_ENDPOINTS from '../config/api';
import { withDeviceIdHeaders } from '../utils/deviceId';
import { getValidStoredInstance } from '../utils/pinSession';
import useParticipantChainState from '../hooks/useParticipantChainState';

const InstanceContext = createContext();

export const useInstance = () => {
  const context = useContext(InstanceContext);
  if (!context) {
    throw new Error('useInstance must be used within an InstanceProvider');
  }
  return context;
};

/** The instance context, or null outside an InstanceProvider (Storybook). */
export const useOptionalInstance = () => useContext(InstanceContext) || null;

/**
 * useInstanceId(searchParams?) — single source of truth for resolving the current instance ID.
 * Checks URL params (kid, instance, instanceId) then falls back to InstanceContext.
 * Use this instead of manually reading searchParams in every screen.
 */
export const useInstanceId = (searchParams) => {
  const { selectedInstanceId, currentInstance, changeInstance } = useInstance();
  const params = searchParams || new URLSearchParams(window.location.search);
  const fromUrl = params.get('kid') || params.get('instance') || params.get('instanceId');
  const resolved = fromUrl || selectedInstanceId || currentInstance?.id || null;

  // Sync context if URL has a different instance
  useEffect(() => {
    if (fromUrl && fromUrl !== selectedInstanceId) {
      changeInstance(fromUrl);
    }
  }, [fromUrl, selectedInstanceId, changeInstance]);

  return resolved;
};

export const InstanceProvider = ({ children }) => {
  const location = useLocation();
  // Check URL parameter first, then participant auth, then fallback to env var
  const getInitialInstanceId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    // Check both 'kid' (participant URLs) and 'instance' (screen URLs)
    const kidParam = urlParams.get('kid') || urlParams.get('instance');
    if (kidParam) {
      console.log('🔗 Using instance ID from URL:', kidParam);
      return kidParam;
    }
    // Check participant auth (logged in with instance PIN) — 4-day TTL
    const participantInstance = getValidStoredInstance();
    if (participantInstance) {
      console.log('🔐 Using instance from participant auth:', participantInstance);
      return participantInstance;
    }
    // Fallback to env var or null
    return import.meta.env.VITE_DEFAULT_INSTANCE_ID || null;
  };

  // Get scenario from URL parameter only (no localStorage)
  const getInitialScenarioId = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const scenarioParam = urlParams.get('scenario');
    if (scenarioParam) {
      console.log('🎯 Using scenario ID from URL:', scenarioParam);
      return scenarioParam;
    }
    return null;
  };

  const initialInstanceId = getInitialInstanceId();
  const initialScenarioId = getInitialScenarioId();

  const [selectedInstanceId, setSelectedInstanceId] = useState(initialInstanceId);
  const [selectedScenarioId, setSelectedScenarioId] = useState(initialScenarioId);
  const [selectedSessionId, setSelectedSessionId] = useState(
    `instance-${initialInstanceId}`
  );
  const [availableSessions, setAvailableSessions] = useState([]);

  // Sync instance from URL when router location changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlInstance = urlParams.get('kid') || urlParams.get('instance');
    if (urlInstance && urlInstance !== selectedInstanceId) {
      console.log('🔗 URL navigation changed instance:', urlInstance);
      setSelectedInstanceId(urlInstance);
      setSelectedSessionId(`instance-${urlInstance}`);
    }
  }, [location.search, selectedInstanceId]);

  // Fetch default instance from database if no instance is set
  useEffect(() => {
    const fetchDefaultInstance = async () => {
      // Only fetch if no instance is currently set
      if (selectedInstanceId) return;

      try {
        const response = await fetch(
          `${API_ENDPOINTS.API_ORIGIN()}/api/settings/default_instance_id`,
          withDeviceIdHeaders()
        );
        if (response.ok) {
          const data = await response.json();
          if (data.value) {
            console.log('🔧 Using default instance from database:', data.value);
            setSelectedInstanceId(data.value);
            setSelectedSessionId(`instance-${data.value}`);
          }
        }
      } catch (error) {
        console.log('Could not fetch default instance from database');
      }
    };

    fetchDefaultInstance();
  }, []); // Only run once on mount

  // Log changes (no localStorage persistence - instance comes from URL or DB)
  useEffect(() => {
    console.log('🔄 Global instance changed:', { selectedInstanceId, selectedSessionId, selectedScenarioId });
  }, [selectedInstanceId, selectedSessionId, selectedScenarioId]);

  // Function to change instance globally
  const changeInstance = (instanceId) => {
    const selectedInstance = availableSessions.find(instance => instance.id === instanceId);
    setSelectedInstanceId(instanceId);
    if (selectedInstance) {
      setSelectedSessionId(`instance-${instanceId}`);
    }

    // Broadcast the change to all components that might be listening
    window.dispatchEvent(new CustomEvent('instanceChanged', {
      detail: { instanceId, sessionId: `instance-${instanceId}` }
    }));
  };

  // Function to change scenario globally
  const changeScenario = (scenarioId) => {
    setSelectedScenarioId(scenarioId);
    console.log('🎯 Scenario changed to:', scenarioId);

    // Broadcast the change to all components that might be listening
    window.dispatchEvent(new CustomEvent('scenarioChanged', {
      detail: { scenarioId }
    }));
  };

  // Fetch current instance data when selectedInstanceId changes
  const [currentInstance, setCurrentInstance] = useState(null);

  useEffect(() => {
    const fetchInstanceData = async () => {
      if (!selectedInstanceId) {
        setCurrentInstance(null);
        return;
      }

      try {
        const response = await fetch(
          API_ENDPOINTS.INSTANCE(selectedInstanceId),
          withDeviceIdHeaders()
        );
        if (response.ok) {
          const data = await response.json();
          setCurrentInstance(data);
        }
      } catch (error) {
        console.error('Failed to fetch instance data:', error);
      }
    };

    fetchInstanceData();
  }, [selectedInstanceId]);

  const refreshCurrentInstance = useCallback(async () => {
    if (!selectedInstanceId) {
      setCurrentInstance(null);
      return;
    }
    try {
      const response = await fetch(
        API_ENDPOINTS.INSTANCE(selectedInstanceId),
        withDeviceIdHeaders()
      );
      if (response.ok) {
        const data = await response.json();
        setCurrentInstance(data);
      }
    } catch (error) {
      console.error('Failed to refresh instance data:', error);
    }
  }, [selectedInstanceId]);

  // The participant menu, entry points and module routes read the chain (#685)
  const participantChain = useParticipantChainState(selectedInstanceId);

  const value = {
    // Alias for convenience (many components use this name)
    instanceId: selectedInstanceId,
    selectedInstanceId,
    selectedSessionId,
    selectedScenarioId,
    availableSessions,
    currentInstance,
    refreshCurrentInstance,
    participantChain: participantChain.chain,
    participantChainError: participantChain.error,
    refreshParticipantChain: participantChain.refresh,
    setAvailableSessions,
    changeInstance,
    changeScenario,
    setSelectedInstanceId,
    setSelectedSessionId,
    setSelectedScenarioId
  };

  return (
    <InstanceContext.Provider value={value}>
      {children}
    </InstanceContext.Provider>
  );
};

export default InstanceContext;